import { removeFalsyValues } from '@shared/helpers';
/*                                               */
import { log } from '@shared/log.browser.ts';
import type { DresonRuleResolvedEvent } from '@shared/types/Tilelist.ts';
import { createOnExFromServerData } from '@tilelist/application/core/OnExFactory.ts';
import type {
    TileListInitialSession,
    TileListSessionContext,
} from '@tilelist/application/core/TileListInitialSession.ts';
import { loadTileListPage } from '@tilelist/application/core/TileListPageLoader.ts';
import type { CacheKeyRefresher } from '@tilelist/application/ports/CacheKeyRefresher.ts';
import type { ClientReloadContextProvider } from '@tilelist/application/ports/ClientReloadContextProvider.ts';
import type { TileListRenderedPublisher } from '@tilelist/application/ports/EventsOutgoing.ts';
import type { TileListPageFetcher } from '@tilelist/application/ports/TileListPageFetcher.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';
import type { PageLoadStrategy } from '@tilelist/domain/composition/strategies/PageLoadStrategy.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';
import { tileListQueryParamSchema } from './TileListRouteValidation.ts';

export type TileListPageLoadResult = {
    /*                                                */
    page: TileListPageLayout | null;
    /*                                                                                  */
    historic: boolean | undefined;
    /*                                                                            */
    shouldScroll: boolean | undefined;
};

export type TileListPageSession = {
    snapshot: TileListSessionContext;
    onex: OnEx;
    /**
 *
 *
 *
 *
 */
    reload: (event: DresonRuleResolvedEvent) => Promise<TileListPageLoadResult>;
};

export type TileListSessionDeps = {
    /*                                                                            */
    pageFetcher: TileListPageFetcher;
    /*                                                                         */
    variationLoadingService: VariationLoader;
    reloadContextProvider: ClientReloadContextProvider;
    eventPublisher: TileListRenderedPublisher;
    /*                                                                 */
    cacheKeyRefresher: CacheKeyRefresher;
    /*                                                                  */
    strategy: PageLoadStrategy;
};

/**
 *
 *
 *
 *
 *
 *
 */
export function createPageSession(
    initialSession: TileListInitialSession,
    deps: TileListSessionDeps,
): TileListPageSession {
    const snapshot = initialSession.session;
    const onex = createOnExFromServerData(snapshot.onexServerData);

    async function reload(event: DresonRuleResolvedEvent): Promise<TileListPageLoadResult> {
        const { rule, page, tsLink, historic, shouldScroll, ...rest } = event;

        /*                                                                                 */
        /*                                               */
        const queryParamParseResult = tileListQueryParamSchema.safeParse(removeFalsyValues({ rule, ...page }));

        if (!queryParamParseResult.success) {
            log.error('Invalid query parameters:', queryParamParseResult.error);
            return { page: null, historic: undefined, shouldScroll: undefined };
        }

        const queryParams = queryParamParseResult.data;

        const reloadContext = await deps.reloadContextProvider.create(snapshot);

        const nextBracketResult = await loadTileListPage({
            mode: 'reload',
            pageFetcher: deps.pageFetcher,
            variationLoadingService: deps.variationLoadingService,
            strategy: deps.strategy,
            ctx: {
                queryParams,
                requestContext: reloadContext,
                onex,
            },
        });

        if (nextBracketResult.state !== 'success') {
            log.error('Failed to load tile list');
            return { page: null, historic, shouldScroll };
        }

        const pageView: TileListPageLayout = nextBracketResult.payload;

        await deps.cacheKeyRefresher.refresh();

        const { rule: extractedRule, ...extractedParams } = queryParams;

        deps.eventPublisher.publishTileListRendered({
            page: extractedParams,
            rule: extractedRule,
            tsLink,
            ...rest,
        });

        return { page: pageView, historic, shouldScroll };
    }

    return { snapshot, onex, reload };
}
